import React, { useState } from 'react';
import { Grid, TextField, Typography, Box, Button, Card, CardContent, Select, MenuItem, FormControl, InputLabel, Checkbox, FormControlLabel, useTheme } from '@mui/material';

import { generateTenantToken, makeEncryptedApiCall } from "./utils/apiUtils";

function BankLoanForm({ setScore, onComputed, setErrorMessage, setOpenSnackbar, setIsLoading, setFixedPay, setVariablePay }) {
  const theme = useTheme();
  const [tenant, setTenant] = useState({ tenantId: '1', tenantName: '' });
  const [form, setForm] = useState({
    loanAmount: '1,00,00,000',
    cibil: '820',
    income: '40,00,000',
    emis: '50,000',
    loanType: 'Home Loan',
    existingCustomer: false,
    documentation: 'Full'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleTenant = (e) => setTenant({ ...tenant, [e.target.name]: e.target.value });
  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });

  const params = [
    { name: 'Credit Quality Score', score: 88, weight: 0.32 },
    { name: 'Debt Service Coverage Ratio (DSCR)', score: 50, weight: 0.22 },
    { name: 'Product-Customer Fit Index', score: 85, weight: 0.16 },
    { name: 'Relationship Depth Multiplier', score: 22, weight: 0.11 },
    { name: 'Documentation Quality Score', score: 95, weight: 0.09 },
    { name: 'Co-lending Risk Assessment', score: 54, weight: 0.07 },
    { name: 'Early Settlement Probability', score: 25, weight: 0.06 },
    { name: 'Collection Ease Score', score: 90, weight: 0.06 }
  ];

  const formula = '(88*0.32) + (90*0.22) + (85*0.16) + (92*0.11) + (95*0.09) + (100*0.07) + (75*0.06) + (90*0.06) = 97.13';

  const handleSubmit = async () => {
    if (!tenant.tenantId.trim() || !tenant.tenantName.trim()) {
      setErrorMessage("Please enter both Tenant ID and Tenant Name.");
      setOpenSnackbar(true);
      setIsSubmitting(false);
      setIsLoading(false);
      return;
    }

    try {
      // Step 1: Generate tenant token
      const tokenResponse = await generateTenantToken(tenant.tenantId, tenant.tenantName);
      
      // Step 2: Make encrypted API call
      const url = `/api/v1.0/tenants/${tenant.tenantId}/score/bank/loan`;
      const score = await makeEncryptedApiCall(url, form, tokenResponse.token);
      
      setScore(score.output);
      const weights = params.reduce((obj, p) => { obj[p.name.replace(/\s+/g, '_').toLowerCase()] = p.weight; return obj; }, {});
    // setScore(scores);
      onComputed && onComputed({ weights, finalScore: 97, finalPay: 14790, formula });
      // setIsSubmitting(false);
      setFixedPay(35000);
      setVariablePay(10000);
    } catch (error) {
      console.error("Error fetching bank score:", error);
      let detailedMessage = "An error occurred while fetching the bank score.";
      if (error.response && error.response.data && error.response.data.error) {
        detailedMessage = error.response.data.error;
      } else if (error.message) {
        detailedMessage = error.message;
      }
      setErrorMessage(detailedMessage);
      setOpenSnackbar(true);
    } finally {
      setIsSubmitting(false);
      setIsLoading(false);
    }
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Card elevation={0} sx={{ mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>Tenant Information</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField name="tenantId" value={tenant.tenantId} onChange={handleTenant} fullWidth size="small" placeholder="Tenant ID" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField name="tenantName" value={tenant.tenantName} onChange={handleTenant} fullWidth size="small" placeholder="Tenant Name" autoComplete="off" />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      {[
        { name: 'loanAmount', label: 'Loan Amount' },
        { name: 'cibil', label: 'CIBIL Score' },
        { name: 'income', label: 'Customer Income' },
        { name: 'emis', label: 'Existing EMIs' },
        { name: 'loanType', label: 'Loan Type' }
      ].map((f) => (
        <Grid item xs={12} sm={6} key={f.name}>
          <Box sx={{ mb: 1 }}>
            <Typography variant="subtitle1" fontWeight={500}>{f.label}</Typography>
          </Box>
          <TextField name={f.name} value={form[f.name]} onChange={handleChange} fullWidth size="small" placeholder={`Enter ${f.label}`} />
        </Grid>
      ))}

      <Grid item xs={12} sm={6}>
        <FormControlLabel control={<Checkbox checked={form.existingCustomer} onChange={handleChange} name="existingCustomer" />} label="Existing Customer?" />
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth size="small">
          <InputLabel id="doc-label">Documentation Quality</InputLabel>
          <Select labelId="doc-label" label="Documentation Quality" name="documentation" value={form.documentation} onChange={handleChange}>
            <MenuItem value="Full">Full</MenuItem>
            <MenuItem value="Partial">Partial</MenuItem>
            <MenuItem value="Inconsistent">Inconsistent</MenuItem>
          </Select>
        </FormControl>
      </Grid>

      <Grid item xs={12}>
      <Button 
        variant="contained" 
        fullWidth 
        size="large"
        onClick={handleSubmit} 
        disabled={isSubmitting} 
        sx={{
          mt: 2, py: 1.5, borderRadius: 2, textTransform: 'none', fontSize: '1rem', fontWeight: 600,
          background: 'linear-gradient(135deg,rgb(88, 139, 249) 0%, #2563EB 100%)',
          boxShadow: '0 10px 20px rgba(37, 99, 235, 0.2)',
          '&:hover': { boxShadow: '0 15px 30px rgba(37, 99, 235, 0.3)', transform: 'translateY(-2px)' },
          transition: 'all 0.3s ease',
          '&.Mui-disabled': {
              background: theme.palette.grey[300],
              boxShadow: 'none',
              color: theme.palette.grey[500],
              cursor: 'not-allowed'
          }
        }}

        >
          {isSubmitting ? 'Calculating...' : 'Calculate Loan Score'}
        </Button>
      </Grid>
    </Grid>
  );
}

export default BankLoanForm;


